import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useForm} from "application/hooks";
import SectionComponent from "./SectionComponent";
import QuestionComponent from "./QuestionComponent";
import {FormComponentProps, Guide, IsBoss, Question, ServiceToClients,} from "../../domain";
import "../styles/SurveyDesign.css";
import {GUIDE_KEYS, RUTAS, SIDEBAR_DESCRIPTIONS, SIDEBAR_TITLE,} from "constants/app";
import {GroupedQuestions} from "../../core";
import iconProceso from "../assets/icon-proceso.png";
import warningIcon from "../assets/warningIcon.svg";
import {useNavigate, useSearchParams} from "react-router-dom";
import FinalErrorModal from "./FinalErrorModal";
import {buildSurveyPayload} from "application/utils/buildSurveyPayload";
import {useSurveyResponse} from "application/hooks/useSurveyResponse";
import {SurveyPayload} from "infrastructure/services/surveyResponse.service";
import {clearLocalDataForToken} from "application/utils/clearLocalDataForToken";
import {classifySubmitError, SubmitErrorKind} from "application/utils/submitErrors";
import {savePendingSubmission} from "application/utils/pendingSubmissions";
import {useTheme} from "../providers/ThemeProvider";

const MENSAJE_ERROR_ENVIO =
    "Lo sentimos, no pudimos guardar tu información por un error de conexión. " +
    "Por favor verifica la conexión a internet y vuelve a intentarlo.";

const SUBMIT_ERROR_MESSAGES: Partial<Record<SubmitErrorKind, string>> = {
    timeout: MENSAJE_ERROR_ENVIO,
    server: MENSAJE_ERROR_ENVIO,
    client: MENSAJE_ERROR_ENVIO,
};

const FormComponent: React.FC<FormComponentProps> = ({
                                                         guiaKey,
                                                         formKey,
                                                         initialAnswers = {},
                                                         initialScores = {},
                                                         onComplete,
                                                         goBack,
                                                         formOrder
                                                     }) => {
    const {theme} = useTheme();
    // Referencia para el contenedor de preguntas
    const contentRef = React.useRef<HTMLDivElement>(null);
    // Obtener las propiedades del formulario desde el hook useForm
    const {
        sections,
        groupedQuestions,
        answers,
        handleAnswer,
        isSectionVisible,
        scale,
        currentGuide,
        sectionProgress,
        activeSection,
        changeActiveSection,
        setScores,
        applySystemAnswers
    } = useForm(guiaKey, formKey, {initialAnswers, initialScores});

    /** Devuelve el título (sections[].title) que corresponde al activeSection actual */
    const getCurrentSectionTitle = useCallback((): string => {
        const progressItem = sectionProgress.find((p) => p.id === activeSection);
        return progressItem ? progressItem.title : activeSection;
    }, [activeSection, sectionProgress]);

    const section2Questions = useMemo(() => ["GI_II_1", "GI_II_2"], []);

    const section3Questions = useMemo(
        () => [
            "GI_III_1",
            "GI_III_2",
            "GI_III_3",
            "GI_III_4",
            "GI_III_5",
            "GI_III_6",
            "GI_III_7",
        ],
        []
    );

    const section4Questions = useMemo(
        () => [
            "GI_IV_1",
            "GI_IV_2",
            "GI_IV_3",
            "GI_IV_4",
            "GI_IV_5",
        ],
        []
    );

    const section1Questions = useMemo(
        () => ["GI_I_1", "GI_I_2", "GI_I_3", "GI_I_4", "GI_I_5", "GI_I_6"],
        []
    );

    const section2to4Questions = useMemo(
        () => [...section2Questions, ...section3Questions, ...section4Questions],
        [section2Questions, section3Questions, section4Questions]
    );

    const [searchParams] = useSearchParams();
    const urlToken = searchParams.get("token");

    // logica para abrir el menu de hamburguesa para la seccion de progreso
    const [menuOpen, setMenuOpen] = useState(false);

    // logica para pintar de rojo las preguntas faltantes
    const [unansweredIds, setUnansweredIds] = useState<Set<string>>(new Set());

    //Logica para mostrar el modal "no se pudieron enviar tus respuestas"
    const [showFinalErrorModal, setShowFinalErrorModal] = useState(false);
    const [finalError, setFinalError] = useState<ReturnType<typeof classifySubmitError> | null>(null);

    const scopedIsBossId = `${formKey}_isBoss`;
    const scopedServiceId = `${formKey}_serviceToClients`;

    // Obtener las propiedades especiales de la guía usando useMemo para optimizar rendimiento
    const {serviceToClients, isBoss} = useMemo(() => {
        const selectedGuia = currentGuide || ({} as Guide);
        return {
            serviceToClients:
                selectedGuia &&
                typeof selectedGuia === "object" &&
                "serviceToClients" in selectedGuia
                    ? (selectedGuia.serviceToClients as ServiceToClients)
                    : null,
            isBoss:
                selectedGuia &&
                typeof selectedGuia === "object" &&
                "isBoss" in selectedGuia
                    ? (selectedGuia.isBoss as IsBoss)
                    : null,
        };
    }, [currentGuide]);

    const dependentQuestionIds = useMemo(() => {
        const ids = new Set<string>();
        if (serviceToClients?.dependentQuestions) {
            serviceToClients.dependentQuestions.forEach((q) => ids.add(q.id));
        }
        if (isBoss?.dependentQuestions) {
            isBoss.dependentQuestions.forEach((q) => ids.add(q.id));
        }
        return ids;
    }, [serviceToClients, isBoss]);

    const filteredGroupedQuestions = useMemo(() => {
        if (
            dependentQuestionIds.size === 0 ||
            Object.keys(groupedQuestions).length === 0
        ) {
            return groupedQuestions;
        }

        const result: GroupedQuestions = {};

        for (const category in groupedQuestions) {
            const domains = groupedQuestions[category];
            const newDomains: GroupedQuestions[string] = {};
            let categoryHasContent = false;

            for (const domain in domains) {
                const dimensions = domains[domain];
                const newDimensions: GroupedQuestions[string][string] = {};
                let domainHasContent = false;

                for (const dimension in dimensions) {
                    const questions = dimensions[dimension];
                    const filteredQuestions = questions.filter(
                        (q: Question) => !dependentQuestionIds.has(q.id)
                    );

                    if (filteredQuestions.length > 0) {
                        newDimensions[dimension] = filteredQuestions;
                        domainHasContent = true;
                    }
                }

                if (domainHasContent) {
                    newDomains[domain] = newDimensions;
                    categoryHasContent = true;
                }
            }

            if (categoryHasContent) {
                result[category] = newDomains;
            }
        }

        return result;
    }, [groupedQuestions, dependentQuestionIds]);

    // utilidades — sirve para Guía I y para las demás
    const getUnansweredIdsForActiveSection = useCallback((): string[] => {

        // ─────  secciones especiales ────────────────────────────────
        if (activeSection === "serviceToClients" && serviceToClients) {
            const ids = [scopedServiceId];

            if (answers[scopedServiceId] === true) {
                serviceToClients.dependentQuestions.forEach((q) => ids.push(q.id));
            } else {
                setShowIncompleteAlert(false);
                setUnansweredIds(new Set());
            }

            return ids.filter((id) => answers[id] === undefined || answers[id] === null);
        }

        if (activeSection === "isBoss" && isBoss) {
            const ids = [scopedIsBossId];

            if (answers[scopedIsBossId] === true) {
                isBoss.dependentQuestions.forEach((q) => ids.push(q.id));
            } else {
                setShowIncompleteAlert(false);
                setUnansweredIds(new Set());
            }

            return ids.filter((id) => answers[id] === undefined || answers[id] === null);
        }

        /* ─── Guías II-III ─── */
        if (filteredGroupedQuestions[activeSection]) {
            const ids: string[] = [];
            Object.values(filteredGroupedQuestions[activeSection]).forEach((domain) =>
                Object.values(domain).forEach((dimension) =>
                    dimension.forEach((q: Question) => ids.push(q.id))
                )
            );
            return ids.filter(
                (id) => answers[id] === undefined || answers[id] === null
            );
        }

        /* ─── Guía I + secciones especiales ─── */
        const sectionTitle = getCurrentSectionTitle();
        const seccion = sections.find((sec) => sec.title === sectionTitle);
        if (seccion) {
            return seccion.questions
                .filter((q) => answers[q.id] === undefined || answers[q.id] === null)
                .map((q) => q.id);
        }
        /* fallback: nada encontrado */
        return [];
    }, [activeSection, answers, sections, filteredGroupedQuestions, getCurrentSectionTitle, isBoss, scopedIsBossId, scopedServiceId, serviceToClients]);

    /* Validar que es la ultima guía
     *
 */
    const isLastGuide = formKey === formOrder[formOrder.length - 1];
    const navigate = useNavigate();

    // Verificar si es la Guía I
    const isGuideI = formKey === GUIDE_KEYS.I;

    // Usar refs para rastrear el estado de renderizado y prevenir bucles infinitos
    const initialRenderRef = React.useRef(true);
    const prevAnswersRef = React.useRef<string>("");

    // Verificar si todas las preguntas de la sección 1 están respondidas
    const isSection1Complete = useMemo(() => {
        if (!isGuideI) return true;
        return section1Questions.every(id => answers[id] !== undefined);
    }, [isGuideI, answers, section1Questions]);

    // Verificar si hay alguna respuesta "Sí" en la sección 1 (preguntas GI_I_1 a GI_I_6)
    const hasAnyYesInSection1 = useMemo(() => {
        if (!isGuideI) return false;
        return section1Questions.some(id => answers[id] === true);
    }, [isGuideI, answers, section1Questions]);

    //Verificar si se han respondido todas las preguntas
    const [showIncompleteAlert, setShowIncompleteAlert] = useState(false);
    const [showIncompleteAlertGuideI, setShowIncompleteAlertGuideI] =
        useState(false);

    const handleNextGuideI = () => {
        const currentIndex = sectionProgress.findIndex(
            (section) => section.id === activeSection
        );

        const isLastSection = currentIndex === sectionProgress.length - 1;

        const isSectionOne = currentIndex === 0;
        const ids = getUnansweredIdsForActiveSection();

        if (
            (isLastSection && ids.length === 0) ||
            (!hasAnyYesInSection1 && isSection1Complete)
        ) {
            onComplete?.();
        } else {
            if (ids.length) {
                setUnansweredIds(new Set(ids));
                setShowIncompleteAlertGuideI(true);
                return;
            }

            // Validar si todas fueron "No" en sección 1
            if (isSectionOne && !hasAnyYesInSection1) {
                setShowIncompleteAlertGuideI(false);
                return;
            }

            // Determinar la siguiente sección
            let nextIndex = currentIndex + 1;

            if (!hasAnyYesInSection1 && nextIndex > 0 && nextIndex < 4) {
                nextIndex = 4;
            }

            if (nextIndex < sectionProgress.length) {
                handleSectionChange(sectionProgress[nextIndex].id);
            }

            setUnansweredIds(new Set());
            setShowIncompleteAlertGuideI(false);
        }
    };

    // Mostrar el estado actual de hasAnyYesInSection1
    useEffect(() => {
        if (isGuideI) {
            console.info("hasAnyYesInSection1 actualizado:", hasAnyYesInSection1);
        }
    }, [hasAnyYesInSection1, isGuideI]);

    // Usar useEffect para manejar cambios en las respuestas y prevenir bucles infinitos
    useEffect(() => {
        // Saltar la primera renderización para prevenir bucles infinitos
        if (initialRenderRef.current) {
            initialRenderRef.current = false;
            return;
        }
        // Solo registrar cuando las respuestas cambian
        const currentAnswersString = JSON.stringify(answers);
        if (currentAnswersString !== prevAnswersRef.current) {
            console.info(
                "FormComponent - answers updated:",
                Object.keys(answers).length
            );
            console.info("Current answers:", currentAnswersString);
            prevAnswersRef.current = currentAnswersString;
        }
    }, [answers]);

    // Agregar un efecto específico para cambios en el formulario/guía
    useEffect(() => {
        console.info(`FormComponent - form changed to: ${formKey}`);
        // Reset the initial render flag when the form changes
        initialRenderRef.current = true;
    }, [formKey, guiaKey]);


    const handleGuideIAnswer = React.useCallback(
        async (questionId: string, value: any) => {
            const isGuideISection1 =
                isGuideI &&
                questionId.startsWith("GI_I_") &&
                ["1", "2", "3", "4", "5", "6"].includes(questionId.split("_")[2]);

            if (!isGuideISection1) {
                await handleAnswer(questionId, value);
                return;
            }

            // Contemos "Sí" existentes ANTES de este click (estado actual en answers).
            const prevYesCount = section1Questions.reduce(
                (n, id) => n + (answers[id] === true ? 1 : 0), 0
            );

            const becomesYes = value === true && answers[questionId] !== true;

            // ── CASO: PRIMER “SÍ”  (pasamos de 0 a 1 Sí)
            if (prevYesCount === 0 && becomesYes) {
                // Persistimos este “Sí” y limpiamos S2–S4 a “sin contestar”
                const s24Clean = Object.fromEntries(section2to4Questions.map(id => [id, null]));
                await applySystemAnswers({
                    [questionId]: true,
                    ...s24Clean
                });

                // Quitamos los scores autollenados (0) en S2–S4
                setScores(prev => {
                    const next = {...prev};
                    section2to4Questions.forEach(id => {
                        delete next[id];
                    });
                    return next;
                });

                return;
            }

            // ── RESTO: guardar normal; el “autollenado cuando queden todas No” lo hace el useEffect de abajo
            await handleAnswer(questionId, value);
        },
        [isGuideI, answers, section1Questions, section2to4Questions, applySystemAnswers, setScores, handleAnswer]
    );


    useEffect(() => {
        if (!isGuideI) return;

        // Foto estable de S1
        const s1Values = section1Questions.map(id => answers[id]);
        const allKnown = s1Values.every(v => v !== undefined);
        const allNo = allKnown && s1Values.every(v => v === false);

        if (!allNo) return;

        // ¿Hace falta autollenar S2–S4? (evita repeticiones)
        const needsFill = section2to4Questions.some(id => answers[id] !== false);
        if (!needsFill) return;

        // Snapshot completo de S1 (incluye el "último No" que faltaba)
        const s1Snapshot = Object.fromEntries(section1Questions.map(id => [id, answers[id]]));
        const s24FillAnswers = Object.fromEntries(section2to4Questions.map(id => [id, false]));
        const s24FillScores = Object.fromEntries(section2to4Questions.map(id => [id, 0]));

        (async () => {
            // Escritura atómica: S1 (todos No) + S2–S4=false
            await applySystemAnswers({...s1Snapshot, ...s24FillAnswers});
            setScores(prev => ({...prev, ...s24FillScores}));
        })();
    }, [isGuideI, answers, section1Questions, section2to4Questions, applySystemAnswers, setScores]);


    const handleAnswerWrapper = useCallback(
        async (
            questionId: string,
            value: boolean,
        ) => {
            try {
                await handleAnswer(questionId, value);
            } catch (error) {
                console.error("Error actualizando la respuesta:", error);
            }
        },
        [handleAnswer]
    );

    const renderDependentQuestions = React.useCallback(
        (questionId: string, dependentQuestions: Question[]) => {
            if (answers[questionId] === true) {
                return dependentQuestions.map((question) => (
                    <QuestionComponent
                        key={question.id}
                        question={question}
                        answer={answers[question.id]}
                        onAnswer={handleAnswerWrapper}
                        scale={scale}
                        isUnanswered={unansweredIds.has(question.id)}
                    />
                ));
            }
            return null;
        },
        [answers, handleAnswerWrapper, scale, unansweredIds]
    );

    // Calcular el progreso total - memoize this calculation
    const {progress} = useMemo(() => {
        const total = sectionProgress.reduce(
            (sum: number, section: { total: number }) => sum + section.total,
            0
        );
        const answered = sectionProgress.reduce(
            (sum: number, section: { answered: number }) => sum + section.answered,
            0
        );
        const progressValue = total > 0 ? (answered / total) * 100 : 0;
        return {
            progress: progressValue,
        };
    }, [sectionProgress]);

    // Función para cambiar de sección
    const handleSectionChange = React.useCallback(
        (sectionId: string) => {
            changeActiveSection(sectionId);

            localStorage.setItem(`NOM035_${formKey}_section`, sectionId);

            // Hacer scroll al inicio del contenido cuando cambia la sección
            setTimeout(() => {
                if (contentRef.current) {
                    contentRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }
            }, 100);
        },
        [changeActiveSection, formKey]
    );

    // Logica para cargar la seccion en la que continuas
    useEffect(() => {
        const savedSection = localStorage.getItem(`NOM035_${formKey}_section`);
        if (savedSection) {
            changeActiveSection(savedSection);
        }
    }, [formKey, changeActiveSection]);

    useEffect(() => {
        if (!showIncompleteAlert) return;

        const unanswered = getUnansweredIdsForActiveSection();
        if (unanswered.length === 0) {
            setShowIncompleteAlert(false);
        }
    }, [answers, showIncompleteAlert, getUnansweredIdsForActiveSection]);

    useEffect(() => {
        if (!showIncompleteAlertGuideI) return;

        const unanswered = getUnansweredIdsForActiveSection();
        if (unanswered.length === 0) {
            setShowIncompleteAlertGuideI(false);
        }
    }, [answers, showIncompleteAlertGuideI, getUnansweredIdsForActiveSection]);

    useEffect(() => {
        if (!unansweredIds.size) return;

        const stillMissing = new Set<string>();
        unansweredIds.forEach((id) => {
            if (answers[id] === undefined || answers[id] === null) {
                stillMissing.add(id);
            }
        });

        if (stillMissing.size !== unansweredIds.size) {
            setUnansweredIds(stillMissing);
        }
    }, [answers, unansweredIds]);

    /*
    * Manejo de hook para enviar respuestas
    * */
    const {enviarGuia, loading, error} = useSurveyResponse();

    const handleNextGuides = async () => {
        const currentIndex = sectionProgress.findIndex(s => s.id === activeSection);
        const pending = getUnansweredIdsForActiveSection();

        if (pending.length) {
            setUnansweredIds(new Set(pending));
            setShowIncompleteAlert(true);
            return;
        }

        const lastSectionId = sectionProgress[sectionProgress.length - 1]?.id;
        const isLastSection = activeSection === lastSectionId;

        if (isLastSection) {
            if (formKey === GUIDE_KEYS.II || formKey === GUIDE_KEYS.III) {
                const guideKey = formKey.replace("GUIA_", "") as "II" | "III";
                const payload = buildSurveyPayload(guideKey);

                if (!payload) {
                    console.warn("No se pudo construir el payload.");
                    setShowFinalErrorModal(true);
                    return;
                }

                try {
                    await enviarGuia(payload);
                    clearLocalDataForToken(payload.token);
                } catch (err) {
                    showSubmitError(err, payload);
                    return;
                }
            }

            onComplete?.();
            return;
        }

        // Si no es la última sección, avanza normalmente
        handleSectionChange(sectionProgress[currentIndex + 1].id);
        setUnansweredIds(new Set());
    };

    function handlePrevious() {
        const currentIndex = sectionProgress.findIndex(
            (section) => section.id === activeSection
        );

        if (currentIndex === 0) {
            if (isGuideI) {
                navigate(`${RUTAS.FORMULARIO}?token=${urlToken}`);
            } else {
                goBack?.();
            }
        } else {
            handleSectionChange(sectionProgress[currentIndex - 1].id);
        }
    }

    useEffect(() => {
        if (menuOpen) {
            document.body.classList.add("no-scroll");
        } else {
            document.body.classList.remove("no-scroll");
        }
    }, [menuOpen]);

    //Funcionalidad de FinalErrorConection
    const [retryPayload, setRetryPayload] = useState<SurveyPayload | null>(null);

    function handleCancel() {
        setShowFinalErrorModal(false);
    }

    function showSubmitError(err: unknown, payload: SurveyPayload) {
        const classified = classifySubmitError(err);
        console.error(`Error al enviar (${classified.kind}${classified.status ? ` ${classified.status}` : ""}):`, err);

        if (classified.retriable) {
            savePendingSubmission(payload);
        }

        setFinalError(classified);
        setRetryPayload(payload);
        setShowFinalErrorModal(true);
    }

    function finishSubmission(token: string) {
        setShowFinalErrorModal(false);
        setFinalError(null);
        setRetryPayload(null);
        clearLocalDataForToken(token);
        onComplete?.();
    }

    function handleRetry() {
        if (!retryPayload || loading) return;
        const {token} = retryPayload;
        enviarGuia(retryPayload)
            .then(() => finishSubmission(token))
            .catch((err) => showSubmitError(err, retryPayload));
    }

    useEffect(() => {
        if (!showFinalErrorModal || !retryPayload) return;
        if (finalError && !finalError.retriable) return;

        const alVolverLaRed = () => handleRetry();
        window.addEventListener("online", alVolverLaRed);
        return () => window.removeEventListener("online", alVolverLaRed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showFinalErrorModal, retryPayload, finalError, loading]);

    // ¿Estamos en la última sección del formulario?
    const isLastSection =
        sectionProgress.findIndex(s => s.id === activeSection) ===
        sectionProgress.length - 1;

    // Etiqueta del botón dependiendo de si es la última sección y última guía
    const buttonLabel = isLastSection
        ? (isLastGuide ? "Finalizar" : "Siguiente cuestionario")
        : "Siguiente";

    return (
        <div className="container">
            <div className="survey-layout">
                {/* Navegación por secciones - Columna izquierda */}
                <div className={`sections-sidebar ${menuOpen ? "open" : ""}`}>
                    <div className="sidebar-header">
                        {SIDEBAR_TITLE[formKey] && (
                            <span className="sidebar-title">{SIDEBAR_TITLE[formKey]}</span>
                        )}
                        <img
                            src={iconProceso}
                            alt="icono proceso"
                            className="sidebar-title-icon"
                        />
                    </div>
                    {SIDEBAR_DESCRIPTIONS[formKey] && (
                        <p className="guide-description">{SIDEBAR_DESCRIPTIONS[formKey]}</p>
                    )}
                    {sectionProgress.map((section) => (
                        <button
                            key={section.id}
                            className={`section-nav-item 
                            ${activeSection === section.id ? "active" : ""}
                            ${section.progress === 100 ? "completed" : ""}`}
                            onClick={() => {
                                // permitimos click si la sección está completada o si es la primera
                                if (section.progress === 100) {
                                    handleSectionChange(section.id);
                                    setMenuOpen(false);
                                }
                            }}
                        >
                            <div className="section-nav-content">
                                <div className="div-img-success">
                                    <span className="section-nav-title">{section.title}</span>
                                    {section.progress === 100 && (
                                        <img
                                            src={theme?.assets.iconSuccess}
                                            alt="icono succes"
                                            className="icon-success"
                                            style={{width: "15px", height: "15px"}}
                                        />
                                    )}
                                </div>
                                <div className="section-nav-progress">
                                    <div
                                        className="section-nav-progress-bar"
                                        style={{width: `${section.progress}%`}}
                                    ></div>
                                </div>
                                <span className="section-nav-count">
                  {section.answered}/{section.total}
                </span>
                            </div>
                        </button>
                    ))}
                </div>
                <div>
                    <div className="header">
                        <div className="header-top">
                            <h1 className="title">Evaluación de factores de riesgo psicosocial</h1>
                            <button className="hamburger-menu" onClick={() => setMenuOpen(!menuOpen)}>
                                <span className="bar"></span>
                                <span className="bar"></span>
                                <span className="bar"></span>
                            </button>
                        </div>

                        <p className="nom-movil">NOM-035-STPS-2018</p>
                        <hr className="linea-header"/>
                        <div className="progress-bar">
                            <div
                                className="progress-bar-fill"
                                style={{width: `${progress}%`}}
                            ></div>
                        </div>
                        {/* Contenido de preguntas - Área central */}
                        <div className="form-container" ref={contentRef}>
                            {sections.length > 0 ? (
                                <>
                                    {sections.map((section, index) => {
                                        // Para Guía I, solo mostrar secciones 2-4 si alguna respuesta de la sección 1 es Sí

                                        const isSection1 = index === 0;
                                        const shouldShowSection = isGuideI
                                            ? isSection1 || hasAnyYesInSection1
                                            : true;

                                        // Si es la sección 1 o hay alguna respuesta "Sí" en la sección 1, mostrar la sección
                                        const isVisible =
                                            shouldShowSection &&
                                            isSectionVisible(section) &&
                                            activeSection === section.title;

                                        return (
                                            <SectionComponent
                                                key={section.title}
                                                section={section}
                                                answers={answers}
                                                onAnswer={
                                                    isGuideI && isSection1
                                                        ? handleGuideIAnswer
                                                        : handleAnswer
                                                }
                                                isVisible={isVisible}
                                                unansweredIds={unansweredIds}
                                            />
                                        );
                                    })}

                                    {/* Botones de navegación para Guía I */}
                                    {isGuideI && (
                                        <div className="navigation">
                                            {showIncompleteAlertGuideI ? (
                                                <>
                                                    <div className="alerta-incompleta">
                                                        <img
                                                            src={warningIcon}
                                                            alt="Advertencia"
                                                            width={24}
                                                            height={24}
                                                        />
                                                        <span>Faltan responder algunas preguntas para continuar.</span>
                                                    </div>
                                                    <div className="boton-derecha">
                                                        <button className="btn"
                                                                data-cy="guide1-next-button"
                                                                onClick={handleNextGuideI}>
                                                            Siguiente
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        className="btn btn-secondary"
                                                        onClick={handlePrevious}
                                                    >
                                                        Anterior
                                                    </button>
                                                    <button
                                                        className={`btn ${
                                                            !hasAnyYesInSection1 &&
                                                            sectionProgress.findIndex(
                                                                (section) => section.id === activeSection
                                                            ) === 0
                                                                ? "btn-disabled"
                                                                : ""
                                                        }`}
                                                        data-cy="guide1-next-button"
                                                        onClick={handleNextGuideI}
                                                    >
                                                        {
                                                            sectionProgress.findIndex(
                                                                (section) => section.id === activeSection
                                                            ) ===
                                                            sectionProgress.length - 1 ||
                                                            (isSection1Complete && !hasAnyYesInSection1)
                                                                ? "Siguiente cuestionario"
                                                                : "Siguiente "
                                                        }
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    {serviceToClients && activeSection === "serviceToClients" && (
                                        <div className="section-card">
                                            <div className="section-header">
                                                <h2 className="section-title">Servicio a clientes</h2>
                                                <p className="section-description">
                                                    Por favor, responda a la siguiente pregunta
                                                </p>
                                            </div>
                                            <QuestionComponent
                                                key={scopedServiceId}
                                                question={{
                                                    id: scopedServiceId,
                                                    text: serviceToClients.question,
                                                    type: "boolean",
                                                    category: "",
                                                    domain: "",
                                                    dimension: "",
                                                }}
                                                answer={answers[scopedServiceId]}
                                                onAnswer={handleAnswerWrapper}
                                                scale={scale}
                                                isUnanswered={unansweredIds.has(scopedServiceId)}
                                            />
                                            {serviceToClients.dependentQuestions &&
                                                renderDependentQuestions(
                                                    scopedServiceId,
                                                    serviceToClients.dependentQuestions
                                                )}
                                        </div>
                                    )}

                                    {isBoss && activeSection === "isBoss" && (
                                        <div className="section-card">
                                            <div className="section-header">
                                                <h2 className="section-title">Jefatura</h2>
                                                <p className="section-description">
                                                    Por favor, responda a la siguiente pregunta
                                                </p>
                                            </div>
                                            <QuestionComponent
                                                key={scopedIsBossId}
                                                question={{
                                                    id: scopedIsBossId,
                                                    text: isBoss.question,
                                                    type: "boolean",
                                                    category: "",
                                                    domain: "",
                                                    dimension: "",
                                                }}
                                                answer={answers[scopedIsBossId]}
                                                onAnswer={handleAnswerWrapper}
                                                scale={scale}
                                                isUnanswered={unansweredIds.has(scopedIsBossId)}
                                            />
                                            {isBoss.dependentQuestions &&
                                                renderDependentQuestions(
                                                    scopedIsBossId,
                                                    isBoss.dependentQuestions
                                                )}
                                        </div>
                                    )}
                                    {Object.keys(filteredGroupedQuestions).map((category) => (
                                        <div
                                            key={category}
                                            className={`section-card ${
                                                activeSection === category ? "" : "hidden"
                                            }`}
                                        >
                                            <div className="section-header">
                                                <h2 className="section-title">
                                                    <div className="div-img-section">
                                                        {category.charAt(0).toUpperCase() +
                                                            category.slice(1).toLowerCase()}
                                                    </div>
                                                </h2>
                                                <p className="section-description">
                                                    Por favor, responda a las siguientes preguntas:
                                                </p>
                                            </div>
                                            {Object.keys(filteredGroupedQuestions[category]).map(
                                                (domain) => (
                                                    <div key={domain}>
                                                        <h3 className="domain-header">
                                                            {domain.charAt(0).toUpperCase() +
                                                                domain.slice(1).toLowerCase()}
                                                        </h3>
                                                        {Object.keys(
                                                            filteredGroupedQuestions[category][domain]
                                                        ).map((dimension) => (
                                                            <div key={dimension}>
                                                                <h4 className="dimension-header">
                                                                    {dimension}
                                                                </h4>
                                                                {filteredGroupedQuestions[category][domain][
                                                                    dimension
                                                                    ].map((question) => (
                                                                    <QuestionComponent
                                                                        key={question.id}
                                                                        question={question}
                                                                        answer={answers[question.id]}
                                                                        onAnswer={
                                                                            (questionId, value) =>
                                                                                handleAnswerWrapper(
                                                                                    questionId,
                                                                                    value,
                                                                                )
                                                                        }
                                                                        scale={scale}
                                                                        isUnanswered={unansweredIds.has(
                                                                            question.id
                                                                        )}
                                                                    />
                                                                ))}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    ))}

                                    <div className="navigation">
                                        {/* Cuando hay alerta: alerta a la izquierda y botón a la derecha */}
                                        {showIncompleteAlert ? (
                                            <>
                                                <div className="alerta-incompleta">
                                                    <img src={warningIcon} alt="Advertencia"/>
                                                    <span>Faltan responder algunas preguntas para continuar.</span>
                                                </div>
                                                <div className="boton-derecha">
                                                    <button
                                                        className="btn"
                                                        data-cy="guides-next-button"
                                                        onClick={handleNextGuides}
                                                        disabled={
                                                            sectionProgress.findIndex(
                                                                (section) => section.id === activeSection
                                                            ) ===
                                                            sectionProgress.length - 1 ||
                                                            (isGuideI &&
                                                                sectionProgress.findIndex(
                                                                    (section) => section.id === activeSection
                                                                ) === 0 &&
                                                                !hasAnyYesInSection1)
                                                        }
                                                    >
                                                        Siguiente
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={handlePrevious}
                                                >
                                                    {sectionProgress.findIndex(
                                                        (section) => section.id === activeSection
                                                    ) === 0
                                                        ? "Anterior cuestionario"
                                                        : "Anterior"}
                                                </button>
                                                <button
                                                    className="btn"
                                                    data-cy="guides-next-button"
                                                    onClick={handleNextGuides}
                                                    disabled={loading}
                                                >
                                                    {loading ? "Enviando..." : buttonLabel}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                            <FinalErrorModal
                                isOpen={showFinalErrorModal}
                                onExit={handleCancel}
                                onRetry={handleRetry}
                                message={finalError ? SUBMIT_ERROR_MESSAGES[finalError.kind] : undefined}
                                canRetry={!finalError || finalError.retriable}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormComponent;
